import type { TaskActivityPayload } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { AgentRunState, type AgentRunStep } from "@common/types/agentRun";
import type { DemoRunKind, DemoScriptedStep } from "./busyClosesDemo.types";

export interface DemoRunCopy {
    month: string;
    account: string;
    clientName: string;
    entityName: string;
    /** First name of the teammate who reviews, pauses or continues. */
    reviewerName: string;
    waitedOnTitle: string;
}

interface ScriptedEvent {
    payload: TaskActivityPayload;
    isHuman?: true;
}

/** A run's opening history (oldest first), its status line, and what the ticker appends next. */
export interface DemoRunScript {
    history: readonly ScriptedEvent[];
    progress: { step: AgentRunStep; detail: string };
    stageLabel: string;
    queue: ReadonlyArray<
        Omit<DemoScriptedStep, "actorId" | "eventId"> & { isHuman?: true }
    >;
}

const stageCompleted = (stageName: string): ScriptedEvent => ({
    payload: { eventType: "stage_completed", metadata: { stageName } },
});

const proposalsPosted = (count: number, stageName: string): ScriptedEvent => ({
    payload: { eventType: "proposals_posted", metadata: { count, stageName } },
});

const categorized = (count: number): TaskActivityPayload => ({
    eventType: "proposal_applied",
    metadata: {
        tool: "transactions_act",
        operation: "bulk_update",
        count,
        isCategoryChange: true,
    },
});

const matchedTransfers = (count: number): TaskActivityPayload => ({
    eventType: "proposal_applied",
    metadata: {
        tool: "transactions_transfer_matches_act",
        operation: "match",
        count,
        isCategoryChange: false,
    },
});

const createdJournalEntries = (count: number): TaskActivityPayload => ({
    eventType: "proposal_applied",
    metadata: {
        tool: "journals_act",
        operation: "create",
        count,
        isCategoryChange: false,
    },
});

const RUN_STARTED: ScriptedEvent = {
    payload: { eventType: "run_started", metadata: {} },
};

const getStageNames = (copy: DemoRunCopy) => ({
    bankFeeds: `Bank feeds for ${copy.entityName}`,
    categorization: `${copy.month} categorization for ${copy.entityName}`,
    reconciliation: `${copy.account} reconciliation`,
    accruals: `${copy.month} accruals for ${copy.entityName}`,
    reporting: `${copy.month} financials for ${copy.clientName}`,
});

const getWorkingQueue = (copy: DemoRunCopy): DemoRunScript["queue"] => {
    const stages = getStageNames(copy);
    return [
        {
            payload: matchedTransfers(4),
            progress: {
                step: "applies_persisted",
                detail: `Matching ${copy.month} transfers into ${copy.account}`,
            },
        },
        {
            payload: stageCompleted(stages.reconciliation).payload,
            progress: {
                step: "stage_updated",
                detail: `Drafting ${copy.month} payroll accrual for ${copy.entityName}`,
            },
        },
        {
            payload: createdJournalEntries(2),
            progress: {
                step: "applies_persisted",
                detail: `Checking accrued expenses against ${copy.month} bills`,
            },
        },
        {
            payload: proposalsPosted(3, stages.accruals).payload,
            progress: {
                step: "proposal_posted",
                detail: `Reviewing ${copy.month} P&L variance for ${copy.entityName}`,
            },
        },
        {
            payload: stageCompleted(stages.accruals).payload,
            progress: {
                step: "stage_updated",
                detail: `Preparing ${copy.month} financials for ${copy.clientName}`,
            },
        },
    ];
};

const waitingOn = (
    copy: DemoRunCopy,
    waitedOnTaskId: string,
): ScriptedEvent => ({
    payload: {
        eventType: "waiting",
        metadata: { waitedOnTaskId, waitedOnTitle: copy.waitedOnTitle },
    },
});

/** The scripted story of one run kind, filled with its client's names. */
export const getDemoRunScript = ({
    kind,
    copy,
    waitedOnTaskId,
}: {
    kind: DemoRunKind;
    copy: DemoRunCopy;
    waitedOnTaskId: string;
}): DemoRunScript => {
    const stages = getStageNames(copy);
    switch (kind) {
        case "working":
            return {
                history: [
                    RUN_STARTED,
                    stageCompleted(stages.bankFeeds),
                    { payload: categorized(23) },
                    stageCompleted(stages.categorization),
                    proposalsPosted(4, stages.reconciliation),
                    { payload: matchedTransfers(3), isHuman: true },
                ],
                progress: {
                    step: "tool_called",
                    detail: `Reconciling ${copy.account} for ${copy.month}`,
                },
                stageLabel: "Reconciliation",
                queue: getWorkingQueue(copy),
            };
        case "waitingOnClient":
            return {
                history: [
                    RUN_STARTED,
                    stageCompleted(stages.bankFeeds),
                    { payload: categorized(18) },
                    stageCompleted(stages.categorization),
                    waitingOn(copy, waitedOnTaskId),
                ],
                progress: {
                    step: "parked",
                    detail: `Waiting on ${copy.clientName} for 3 receipts over $500`,
                },
                stageLabel: "Documents",
                queue: [],
            };
        case "paused":
            return {
                history: [
                    RUN_STARTED,
                    stageCompleted(stages.bankFeeds),
                    { payload: categorized(11) },
                    {
                        payload: { eventType: "run_paused", metadata: {} },
                        isHuman: true,
                    },
                ],
                progress: {
                    step: "paused",
                    detail: `Paused by ${copy.reviewerName} before the ${copy.account} reconciliation`,
                },
                stageLabel: "Reconciliation",
                queue: [
                    {
                        payload: { eventType: "run_continued", metadata: {} },
                        isHuman: true,
                        shouldResume: true,
                        progress: {
                            step: "resumed",
                            detail: `Reconciling ${copy.account} for ${copy.month}`,
                        },
                    },
                    ...getWorkingQueue(copy),
                ],
            };
        case "needsReview":
            return {
                history: [
                    RUN_STARTED,
                    stageCompleted(stages.bankFeeds),
                    proposalsPosted(6, stages.categorization),
                    waitingOn(copy, waitedOnTaskId),
                ],
                progress: {
                    step: "proposal_posted",
                    detail: `Waiting on review of 6 proposals for ${copy.entityName}`,
                },
                stageLabel: "Categorization",
                queue: [
                    {
                        payload: categorized(2),
                        isHuman: true,
                        progress: {
                            step: "wait_resolved",
                            detail: `Waiting on review of 4 proposals for ${copy.entityName}`,
                        },
                    },
                    {
                        payload: categorized(3),
                        isHuman: true,
                        progress: {
                            step: "wait_resolved",
                            detail: `Waiting on review of 1 proposal for ${copy.entityName}`,
                        },
                    },
                ],
            };
        case "finished":
            return {
                history: [
                    RUN_STARTED,
                    stageCompleted(stages.categorization),
                    { payload: matchedTransfers(6) },
                    stageCompleted(stages.reconciliation),
                    stageCompleted(stages.reporting),
                    {
                        payload: {
                            eventType: "run_finished",
                            metadata: {
                                state: AgentRunState.done,
                                finishReason: null,
                            },
                        },
                    },
                ],
                progress: {
                    step: "finished",
                    detail: `Delivered ${copy.month} financials for ${copy.entityName}`,
                },
                stageLabel: "Reporting",
                queue: [],
            };
    }
};
